import { useEffect, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { PageHeader, StatusBadge, EmptyState } from "../../components/dashboard/Shared";

const Row = ({ k, v, mono = true }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-1.5 text-xs">
    <span className="text-gray-500 shrink-0">{k}</span>
    <span className={`${mono ? "tabular" : ""} text-gray-200 truncate`}>{v ?? "—"}</span>
  </div>
);

export default function Debug() {
  const [accounts, setAccounts] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/debug/mt5").then(({ data }) => setAccounts(data)).catch(() => setAccounts([]));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  if (!accounts) return <div className="skeleton h-96" />;

  return (
    <div>
      <PageHeader title="MetaApi Debug" sub="Live connection diagnostics — auto-refreshes every 5s">
        <button data-testid="debug-refresh-button" onClick={load} className="btn-outline px-4 py-2 text-xs flex items-center gap-2"><RefreshCw size={13} /> Refresh</button>
      </PageHeader>
      {accounts.length === 0 ? (
        <EmptyState title="No MT5 accounts" sub="Accounts appear here as soon as an investor starts a MetaApi connection." />
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {accounts.map((a) => (
            <div key={a.account_id} className="card-dark p-5" data-testid={`debug-account-${a.account_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-white text-sm">{a.owner?.name} <span className="text-gray-500">· {a.owner?.email}</span></p>
                  <p className="text-xs text-gray-500 tabular">{a.broker} · {a.login} · {a.server}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>
              <Row k="MetaApi Account ID" v={a.metaapi_account_id} />
              <Row k="Deployment Status" v={a.deployment_state} />
              <Row k="Synchronization Status" v={a.sync_state} />
              <Row k="Stage" v={a.stage} />
              <Row k="Last Action" v={a.last_action} />
              <Row k="Last Synced" v={a.last_synced_at ? String(a.last_synced_at).slice(0, 19).replace("T", " ") : null} />
              <div className="mt-3">
                <p className="label-xs mb-1">Last API Response</p>
                <pre data-testid={`debug-last-response-${a.account_id}`} className="text-[11px] text-[#00F0FF] bg-black/40 border border-white/10 rounded p-3 whitespace-pre-wrap break-all max-h-32 overflow-auto">{a.last_api_response || "—"}</pre>
              </div>
              <div className="mt-3">
                <p className="label-xs mb-1">Last Error</p>
                <pre data-testid={`debug-last-error-${a.account_id}`} className="text-[11px] text-[#FF3B30] bg-black/40 border border-white/10 rounded p-3 whitespace-pre-wrap break-all max-h-32 overflow-auto">{a.last_error || "None"}</pre>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
